import { Grid, makeStyles } from '@material-ui/core'
import {
  FormSection,
  FormSectionTitle,
  Typography,
  Spinner,
  Button,
  Checkbox,
  FormControlLabel,
  InputField,
  useDialogs,
  ApiKey
} from '@paintscout/ui'
import { useFormikContext } from 'formik'
import { useSnackbar } from 'notistack'
import type { ApiKeyDocument, ClientMetaDocument, OptionsDocument } from 'paintscout'
import React, { useState } from 'react'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '@paintscout/ui'
import {
  useGetApiKeysQuery,
  useGenerateApiKeyMutation,
  useUpdateApiKeyMutation,
  useDeactivateApiKeyMutation
} from '@paintscout/api'
import { ChangeEstimatorDialog } from '@paintscout/react-quote'
import omit from 'lodash/omit'
import { useAuth } from 'react-auth0'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    mainFormSection: {
      padding: theme.spacing(2)
    },
    generateButton: {
      width: '100%',
      marginTop: theme.spacing(1)
    },
    createForm: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius
    },
    createFormButtons: {
      display: 'flex',
      gap: theme.spacing(1),
      marginTop: theme.spacing(2)
    },
    infoContainer: {
      marginTop: theme.spacing(2)
    }
  }),
  { name: 'ClientApiKeys' }
)

export interface ClientApiKeysFormProps {
  classes?: StyleClasses<typeof useStyles>
}

const ClientApiKeys = (props: ClientApiKeysFormProps) => {
  const classes = useStyles(props)
  const [showArchived, setShowArchived] = useState(false)
  const {
    values: { meta }
  } = useFormikContext<{ meta: ClientMetaDocument; options: OptionsDocument }>()
  const [generateApiKey] = useGenerateApiKeyMutation()
  const [updateApiKey] = useUpdateApiKeyMutation()
  const [deactivateApiKey] = useDeactivateApiKeyMutation()
  const [changeMade, setChangeMade] = useState<boolean>(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { openDialog, dismissDialog } = useDialogs()
  const { session } = useAuth()
  const isSuperadmin = session?.app_metadata?.roles?.includes('superadmin')
  const {
    data: apiKeysData,
    loading,
    error,
    refetch
  } = useGetApiKeysQuery({
    variables: {
      companyId: meta._id
    },
    fetchPolicy: changeMade ? 'network-only' : 'cache-and-network'
  })

  const handleNewKey = async () => {
    openDialog(ChangeEstimatorDialog, {
      queryOverride: `app_metadata.companyId:"${meta._id}" AND app_metadata.active:true`,
      onConfirm: async (user) => {
        try {
          await generateApiKey({
            variables: {
              companyId: meta._id,
              userId: user.user_id,
              name: newKeyName.trim() || undefined
            }
          })
          setChangeMade(true)
          refetch()
          setShowCreateForm(false)
          setNewKeyName('')
          enqueueSnackbar(`Successfully generated new apiKey`, { variant: 'success' })
        } catch (error) {
          console.log('error', error)
          enqueueSnackbar(`Error generating new apiKey ${error}`, { variant: 'error' })
        }
        dismissDialog()
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  const handleArchiveApiKey = async (apiKey: ApiKeyDocument) => {
    const res = await deactivateApiKey({
      variables: {
        id: apiKey._id
      }
    })
    if (res) {
      enqueueSnackbar(`Successfully archived apiKey`, { variant: 'success' })
      refetch()
    } else {
      enqueueSnackbar(`Error archiving apiKey`, { variant: 'error' })
    }
  }

  const handleEditApiKey = (apiKey: ApiKeyDocument) => {
    openDialog(ChangeEstimatorDialog, {
      queryOverride: `app_metadata.companyId:"${meta._id}" AND app_metadata.active:true`,
      currentEstimatorId: apiKey?.owner?.id,
      onConfirm: async (user) => {
        if (user.user_id !== apiKey?.owner?.id) {
          const res = await updateApiKey({
            variables: {
              apiKey: {
                ...omit(apiKey, ['__typename']),
                _id: apiKey._id,
                owner: {
                  id: user.user_id,
                  email: user.email,
                  firstName: user?.user_metadata?.firstName ?? user?.name?.split(' ')[0],
                  lastName: user?.user_metadata?.lastName ?? user?.name?.split(' ')[1],
                  phoneNumber: user?.user_metadata?.phoneNumber ?? ''
                }
              }
            }
          })
          if (res) {
            enqueueSnackbar(`Successfully updated apiKey`, { variant: 'success' })
            refetch()
          } else {
            enqueueSnackbar(`Error updating apiKey`, { variant: 'error' })
          }
        }
        dismissDialog()
      },
      onCancel: () => dismissDialog()
    })
  }

  return (
    <>
      <FormSectionTitle title="Api Keys" />
      <FormSection className={classes.mainFormSection}>
        <Grid container spacing={2}>
          {loading && !apiKeysData?.getApiKeys && (
            <Grid item xs={12}>
              <Spinner />
            </Grid>
          )}
          {error && (
            <Grid item xs={12}>
              <Typography variant="h6">Error retrieving client Api Keys.</Typography>
            </Grid>
          )}
          {apiKeysData?.getApiKeys &&
            (apiKeysData.getApiKeys.length === 0 ? (
              <Grid item xs={12}>
                <Typography variant="h6">Client has no Api Keys</Typography>
              </Grid>
            ) : (
              apiKeysData.getApiKeys
                .reduce((prev, current) => {
                  // List active keys first
                  if (current.archived && !showArchived) {
                    return prev
                  } else if (current.archived) {
                    prev.push(current)
                  } else {
                    prev.unshift(current)
                  }
                  return prev
                }, [])
                .map((apiKey) => (
                  <Grid item xs={12} key={apiKey._id}>
                    <ApiKey
                      apiKey={apiKey}
                      actions={[
                        { key: 'edit', label: 'Edit' },
                        isSuperadmin ? { key: 'archive', label: 'Deactivate', scary: true } : null
                      ].filter(Boolean)}
                      onAction={(key) => {
                        if (key === 'edit') handleEditApiKey(apiKey)
                        if (key === 'archive' && isSuperadmin) handleArchiveApiKey(apiKey)
                      }}
                    />
                  </Grid>
                ))
            ))}

          <Grid item xs={12}>
            {showCreateForm ? (
              <div className={classes.createForm}>
                <Typography variant="h6">Create New API Key</Typography>
                <InputField
                  label="Key Name"
                  placeholder="e.g., Production Key"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  fullWidth
                />
                <div className={classes.createFormButtons}>
                  <Button onClick={handleNewKey}>Generate Key</Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowCreateForm(false)
                      setNewKeyName('')
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outlined" className={classes.generateButton} onClick={() => setShowCreateForm(true)}>
                + Generate New API Key
              </Button>
            )}
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox value={showArchived} onClick={() => setShowArchived((prev) => !prev)} />}
              label="Show Inactive Keys"
            />
          </Grid>
        </Grid>
      </FormSection>
    </>
  )
}

export default ClientApiKeys
